import {createContext} from "react";
import {FormattedNumber, useIntl} from "react-intl";
import {useContext} from "preact/hooks";

export type TemperatureContext = {
    converter: (temperatureInCelsius: number) => number;
    toCelsius: (value: number) => number;
    deltaFromCelsius: (temperatureDeltaInCelsius: number) => number,
    unit: string;
    id: string;
};

export const Celsius: TemperatureContext = {
    converter: (temperatureInCelsius: number) => temperatureInCelsius,
    toCelsius: (temperatureInCelsius: number) => temperatureInCelsius,
    deltaFromCelsius: (temperatureDeltaInCelsius: number) => temperatureDeltaInCelsius,
    unit: "°C",
    id: "celsius",
};

export const Fahrenheit: TemperatureContext = {
    converter: (temperatureInCelsius: number) => temperatureInCelsius * 9/5 + 32,
    toCelsius: (temperatureInFahrenheit: number) => (temperatureInFahrenheit - 32) * 5/9,
    deltaFromCelsius: (temperatureDeltaInCelsius: number) => temperatureDeltaInCelsius * 9/5,
    unit: "°F",
    id: "fahrenheit",
};

export const Kelvin: TemperatureContext = {
    converter: (temperatureInCelsius: number) => temperatureInCelsius + 273.15,
    toCelsius: (temperatureInKelvin: number) => temperatureInKelvin - 273.15,
    deltaFromCelsius: (temperatureDeltaInCelsius: number) => temperatureDeltaInCelsius,
    unit: "K",
    id: "kelvin"
};

export function getTemperatureById(id: string): TemperatureContext {
    for (const ctx of [Celsius, Fahrenheit, Kelvin]) {
        if (ctx.id === id) {
            return ctx;
        }
    }

    throw new Error(`Temperature ${id} was not found.`);
}

export const TemperatureContext = createContext<TemperatureContext>(Celsius);

export type TemperatureValue = {
    value: number | null;
    convert?: boolean;
    forceSign?: boolean;
};

export function useTemperature() {
    const ctx = useContext(TemperatureContext);
    const intl = useIntl();

    return {
        format: (value?: number | null, convert: boolean = true, forceSign: boolean = false) => {
            if (value === null || value === undefined) {
                return `${intl.formatNumber(0, {maximumFractionDigits: 1, minimumFractionDigits: 1}).replace(/0/g, "-")} ${ctx.unit}`;
            } else {
                return `${value > 0 && forceSign ? "+" : ""}${intl.formatNumber(convert ? ctx.converter(value) : value, {maximumFractionDigits: 1, minimumFractionDigits: 1})} ${ctx.unit}`;
            }
        },
        convert: ctx.converter,
        unit: ctx.unit,
        toCelsius: ctx.toCelsius,
        deltaFromCelsius: ctx.deltaFromCelsius,
    }
}

export function Temperature(props: TemperatureValue) {
    const temp = useTemperature();
    return <>{temp.format(props.value, props.convert ?? true, props.forceSign)}</>;
}
